import { trackButtonClicked } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { Button } from '@heidi/design-system/button';
import { TemplatePages } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { SupportedLocale } from '@heidi/utils/i18n';
import { Loader2, Plus as PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface AddToLibraryButtonProps {
  templateId?: string;
  templateAlreadyExists: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'touch';
  className?: string;
  host: {
    hasUser: boolean;
    isAuthenticated: boolean;
    tutorialStepCurrentPage?: { enum?: string };
    panelOpen: boolean;
    isPending: boolean;
    importTemplateFromGallery: (templateId: string) => void;
    onUnauthenticatedClick: (templateId: string) => void;
  };
}

export const AddToLibraryButton = ({
  templateId,
  templateAlreadyExists,
  size = 'md',
  className,
  host,
}: AddToLibraryButtonProps) => {
  const { locale: intlLocale } = useIntl();
  const locale = intlLocale as SupportedLocale;
  const {
    hasUser,
    isAuthenticated,
    tutorialStepCurrentPage,
    panelOpen,
    isPending,
    importTemplateFromGallery,
    onUnauthenticatedClick,
  } = host;

  const shouldHighlightAddToLibrary = useMemo(
    () =>
      tutorialStepCurrentPage?.enum === TemplatePages.ADD_TO_YOUR_LIBRARY && panelOpen,
    [panelOpen, tutorialStepCurrentPage?.enum]
  );

  const handleClick = () => {
    if (!templateId) {
      return;
    }
    trackButtonClicked({
      screenName: ScreenName.TemplateCommunityDetails,
      action: ButtonAction.CommunityTemplateImport,
      label: isAuthenticated ? 'Add to my library' : 'Use Template',
    });
    if (isAuthenticated) {
      importTemplateFromGallery(templateId);
      return;
    }
    onUnauthenticatedClick(templateId);
  };

  return (
    <Button
      data-testid="community-add-to-library-button"
      size={size}
      className={cn(
        locale === SupportedLocale.DeDe && 'text-xs',
        shouldHighlightAddToLibrary && 'animation-ping',
        className
      )}
      onClick={handleClick}
      disabled={templateAlreadyExists || (isAuthenticated && isPending)}
    >
      {isAuthenticated && isPending ? <Loader2 className="animate-spin" /> : <PlusIcon />}
      {templateAlreadyExists ? (
        <FormattedMessage
          defaultMessage="Already in your library"
          id="+geA9TIYUn"
          description="Button label when a community template is already in the user’s library"
        />
      ) : hasUser ? (
        <FormattedMessage
          defaultMessage="Add to my library"
          id="4e6MHpbUaF"
          description="Button label for adding a community template to the user’s library"
        />
      ) : (
        <FormattedMessage
          defaultMessage="Use template"
          id="VM1qiCYtr3"
          description="Button label for signing in to use a community template"
        />
      )}
    </Button>
  );
};
